import React, { useState, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Fade,
} from "@mui/material";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { AuthContext } from "../../../context/AuthContext/AuthContext";

/**
 * Pantalla para ingresar un código de recuperación durante el login cuando
 * el usuario no tiene acceso a su app authenticator.
 *
 * Props:
 *   onSuccess(result) — callback tras uso exitoso; result = { userData,
 *     codesRemaining, warning }. LoginPage decide la navegación.
 *   onBackToTotp() — regresar al step de verificación TOTP normal.
 */
const MfaRecoveryForm = ({ onSuccess, onBackToTotp }) => {
  const { authWithRecoveryCode } = useContext(AuthContext);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    setError(null);
    const trimmed = code.trim();
    if (!trimmed) {
      setError("Please enter a recovery code.");
      return;
    }
    setLoading(true);
    try {
      const result = await authWithRecoveryCode(trimmed);
      onSuccess?.(result);
    } catch (err) {
      const resCode = err?.response?.data?.result;
      const message = err?.response?.data?.message || err?.message;
      if (resCode === 3) {
        setError("Invalid or already used recovery code.");
      } else if (resCode === 2) {
        setError("2FA is not active for this user.");
      } else {
        setError(message || "Could not validate the recovery code.");
      }
      setCode("");
    } finally {
      setLoading(false);
    }
  }, [code, authWithRecoveryCode, onSuccess]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && code.trim() && !loading) {
      handleSubmit();
    }
  };

  return (
    <Fade in timeout={400}>
      <Box>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <VpnKeyOutlinedIcon sx={{ fontSize: 44, color: "#0C343D", mb: 0.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0C343D" }}>
            Recovery Code
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
            Enter one of the 8 recovery codes you saved when you activated 2FA.
          </Typography>
        </Box>

        {error && (
          <Fade in>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          </Fade>
        )}

        <TextField
          autoFocus
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="xxxx-xxxx-xxxx"
          inputProps={{
            style: {
              textAlign: "center",
              fontSize: "1.15rem",
              letterSpacing: "0.1rem",
              fontFamily: "monospace",
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "#fafafa",
              "&:hover fieldset": { borderColor: "#00bcd4" },
              "&.Mui-focused fieldset": { borderColor: "#0C343D" },
            },
          }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={!code.trim() || loading}
          sx={{
            mt: 2.5,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "none",
            bgcolor: "#0C343D",
            boxShadow: "0 4px 12px rgba(12,52,61,0.3)",
            "&:hover": {
              bgcolor: "#0a2a33",
              boxShadow: "0 6px 16px rgba(12,52,61,0.4)",
            },
            "&.Mui-disabled": { bgcolor: "#ccc" },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Use Code"}
        </Button>

        <Typography variant="body2" sx={{ mt: 2, textAlign: "center", color: "#666" }}>
          <Link
            component="button"
            type="button"
            onClick={onBackToTotp}
            sx={{
              color: "#0C343D",
              fontWeight: 500,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline", color: "#00bcd4" },
            }}
          >
            Back to authenticator code
          </Link>
        </Typography>
      </Box>
    </Fade>
  );
};

export default MfaRecoveryForm;
